import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { post } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/common/PasswordInput";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [masterPassword, setMasterPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const goStep2 = (e) => {
    e.preventDefault();
    setError("");
    if (!email) { setError("Email is required"); return; }
    setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!masterPassword) { setError("Master password is required"); return; }
    if (newPassword.length < 8) { setError("New password must be at least 8 characters"); return; }
    if (newPassword !== confirmPassword) { setError("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      await post("/auth/forgot-password", { email, master_password: masterPassword, new_password: newPassword });
      toast.success("Password reset. You can sign in with your new password now.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not reset password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center justify-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#3A86FF] font-display text-lg font-bold text-white">I</div>
          <span className="font-display text-xl font-bold text-foreground">Iris</span>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <h1 className="font-display text-lg font-semibold text-foreground">Reset password</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {step === 1
              ? "Enter your account email to continue."
              : "No email/OTP integration exists yet — enter the admin master password to verify it's really you, then set a new password."}
          </p>

          {step === 1 ? (
            <form className="mt-6 space-y-4" onSubmit={goStep2}>
              <div>
                <Label htmlFor="fp-email">Email</Label>
                <Input id="fp-email" type="email" autoComplete="username" className="mt-1"
                  data-testid="forgot-password-email-input" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
              <Button type="submit" className="w-full" data-testid="forgot-password-next-button">Continue</Button>
            </form>
          ) : (
            <form className="mt-6 space-y-4" onSubmit={submit}>
              <div>
                <Label htmlFor="fp-master">Master Password</Label>
                <PasswordInput id="fp-master" className="mt-1"
                  data-testid="forgot-password-master-input" value={masterPassword} onChange={(e) => setMasterPassword(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="fp-new">New Password</Label>
                <PasswordInput id="fp-new" autoComplete="new-password" className="mt-1"
                  data-testid="forgot-password-new-input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="fp-confirm">Confirm New Password</Label>
                <PasswordInput id="fp-confirm" autoComplete="new-password" className="mt-1"
                  data-testid="forgot-password-confirm-input" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
              </div>
              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
              <div className="flex gap-2">
                <Button type="button" variant="ghost" className="flex-1" onClick={() => setStep(1)}>Back</Button>
                <Button type="submit" className="flex-1" data-testid="forgot-password-submit-button" disabled={submitting}>
                  {submitting ? "Resetting..." : "Reset Password"}
                </Button>
              </div>
            </form>
          )}

          <div className="mt-4 text-center">
            <Link to="/login" className="text-sm text-primary hover:underline">Back to sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
